import { ApolloError } from '@apollo/client';

import { OpsOrderCarrierService } from '@/graphql/generatedTypes';
import i18n from '@/utils/i18n';

import type { CarrierEntry } from './DefaultCarrierConfig';

/**
 * Extracts a human-readable reason from an Apollo mutation error.
 *
 * Backend rejections (e.g. INVALID_INPUT) surface as GraphQL errors where the
 * top-level `message` is just the error code ("INVALID_INPUT") and the human
 * sentence is buried in `extensions.exception.reason`. We dig that out first
 * and only fall back to the code / generic toast string.
 */
export function extractCarrierErrorMessage(err: unknown): string {
  if (err instanceof ApolloError) {
    for (const gqlErr of err.graphQLErrors ?? []) {
      const ext = gqlErr.extensions as
        | {
            exception?: { reason?: unknown; error?: unknown };
            reason?: unknown;
          }
        | undefined;
      const exceptionReason = ext?.exception?.reason;
      if (typeof exceptionReason === 'string' && exceptionReason.trim()) {
        return exceptionReason;
      }
      if (typeof ext?.reason === 'string' && ext.reason.trim()) {
        return ext.reason;
      }
    }
    const firstMessage = err.graphQLErrors?.[0]?.message;
    if (firstMessage && firstMessage !== 'INVALID_INPUT') return firstMessage;
  }
  if (err instanceof Error && err.message) return err.message;
  return i18n.t('Carrier:saveError');
}

/**
 * Backend does not yet support Pickup — strip any stale entries so we never
 * round-trip them through the UI into a save payload.
 */
export function withoutUnsupportedServices(
  entries: CarrierEntry[],
): CarrierEntry[] {
  return entries.filter(
    (e) => e.carrier_service !== OpsOrderCarrierService.Pickup,
  );
}
